import React from "react";
import { IconComponent } from "../../../components/Icon";
import TypographyInline from "../../../components/Typography/TypographyInline";
import { ICON_LABELS } from "../../../utils/constants/UI/iconLabels";
import { WARNING, BRAND, GREY, SUCCESS, BASE } from "../../../utils/constants/colors";
import styles from "../campaignPipeline.module.css";

const TrackBar = ({ steps, currentPhase, isPaused = false, onStepClick }) => {
  const isInterrupted = currentPhase === "interrupted";
  const activeIndex = steps.findIndex((s) => s.id === currentPhase);
  // For interrupted, find the last step that was completed before interruption
  // (interrupted won't match any step, so activeIndex will be -1 - treat all steps as pending but show badge)
  const effectiveActiveIndex = isInterrupted ? -1 : activeIndex;

  return (
    <div className={styles.stepperCompact}>
      {steps.map((step, i) => {
        const isDone = isInterrupted ? false : i < effectiveActiveIndex;
        const isActive = isInterrupted ? false : i === effectiveActiveIndex;

        const pillClass = isDone
          ? styles.stepPillDone
          : isActive
            ? styles.stepPillActive
            : styles.stepPillPending;

        const dotClass = isDone
          ? styles.stepDotDone
          : isActive
            ? styles.stepDotActive
            : styles.stepDotPending;

        const labelColor = isDone
          ? SUCCESS.W_700
          : isActive
            ? BRAND.PRIMARY
            : GREY.DISABLED;

        // Show paused badge right after the active step's connector
        const showPausedAfter = isPaused && isActive && i < steps.length - 1;

        const isClickable = isDone && !!onStepClick;

        return (
          <React.Fragment key={step.id}>
            <div
              className={styles.stepNode}
              onClick={isClickable ? () => onStepClick(step.id) : undefined}
              style={isClickable ? { cursor: "pointer" } : undefined}
            >
              <div className={`${styles.stepPill} ${pillClass}`}>
                <div
                  className={`${styles.stepDot} ${dotClass}`}
                  key={isDone ? `done-${step.id}` : step.id}
                >
                  {isDone ? (
                    <IconComponent
                      iconLabel={ICON_LABELS.CHECK}
                      color={BASE.WHITE}
                      fontSize="11px"
                    />
                  ) : isActive && step.variant === "spinner" ? (
                    <div className={styles.stepDotSpinner} />
                  ) : (
                    i + 1
                  )}
                </div>
                <TypographyInline
                  size="sm"
                  fontWeight={isActive || isDone ? 600 : 500}
                  color={labelColor}
                  label={step.label}
                />
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`${styles.stepLine} ${isDone ? styles.stepLineDone : styles.stepLinePending}`}
                />
              )}
            </div>

            {/* Paused pill badge - inline between the paused step and next step */}
            {showPausedAfter && (
              <div className={styles.pausedBadge}>
                <div className={styles.pausedBadgeIcon}>
                  <span />
                  <span />
                </div>
                <TypographyInline
                  size="xs"
                  fontWeight={600}
                  color={WARNING.W_700}
                  label="Paused"
                />
              </div>
            )}
          </React.Fragment>
        );
      })}

      {/* Interrupted badge - shown at the end of the track bar */}
      {isInterrupted && (
        <div className={styles.interruptedBadge}>
          <div className={styles.interruptedBadgeIcon}>
            <IconComponent
              iconLabel={ICON_LABELS.ERROR}
              color="#FF3B3B"
              fontSize="14px"
            />
          </div>
          <TypographyInline
            size="xs"
            fontWeight={600}
            color="#FF3B3B"
            label="Interrupted"
          />
        </div>
      )}
    </div>
  );
};

export default TrackBar;
